import { useEffect, useRef, useState } from "react";
import { Buton, Ikon, Kart, NotKutusu, Rozet, anMetni, cn, type RozetTonu } from "@koclukpanel/ui";
import { AdresFormu } from "./AdresFormu";
import { siparisAdresVer, siparisGetir, siparisIptalEt } from "./api-magaza";
import { hataMetni } from "./hata-metni";
import {
  SIPARIS_DURUMU_ETIKETI,
  SIPARIS_IPTAL_NEDENI_ETIKETI,
  type Siparis,
  type SiparisAdresi,
  type SiparisDurumu,
} from "./magaza-tipleri";

/**
 * SİPARİŞ AYRINTISI — F-GAM-17, oyunlaştırma dalga 2 / sürüm 7a.
 *
 * Bütün durum ve izin kararları SUNUCUDAN: ekran "iptal edilebilir mi", "adres gerekli mi", "18 altı mı"
 * HESAPLAMAZ; `iptalEdilebilir` ve `durum` ne diyorsa onu çizer. Adres bilgisi bu ekrana HİÇ gelmez
 * (`Siparis` tipinde yalnız `adresVerildi` var) ve burada gösterilmez.
 *
 * Bu dosya `Magaza` ve `Siparislerim` ekranlarının ortak parçalarını da dışa aktarır (adres bölümü, tarih biçimi,
 * rozet tonu): iki yerde iki kopya olsaydı biri geride kalırdı.
 */

/** Veli/adres "son gün" için takvim günü: Europe/Istanbul. Anlık bir zaman damgasını UTC günüyle yazmak yanlış güne düşebilir. */
export function gunBicimi(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("tr-TR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Europe/Istanbul",
  }).format(d);
}

export function siparisRozetTonu(durum: SiparisDurumu): RozetTonu {
  switch (durum) {
    case "AWAITING_PARENT":
    case "AWAITING_ADDRESS":
      return "uyari";
    case "PREPARING":
    case "SHIPPED":
      return "bilgi";
    case "DELIVERED":
      return "onay";
    case "CANCELLED":
      return "hata";
  }
}

/** Görünüm değişince (mağaza → ürün, liste → ayrıntı) kısa ekranda sayfanın ortasında kalmasın. */
export function useUsteKaydir() {
  const kok = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    kok.current?.scrollIntoView?.({ block: "start" });
  }, []);
  return kok;
}

/**
 * Adres bölümü: `Magaza` (sipariş hemen sonrası) ve `SiparisDetay` (sonradan dönünce) AYNI akışı kullanır.
 * Yalnız 18 ve üstü öğrenci adres verir; 18 altında adresi veli verir ve bu bölüm hiç çizilmez (durum sunucudan).
 */
export function AdresBolumu({
  siparis,
  onGuncel,
}: {
  siparis: Siparis;
  onGuncel: (s: Siparis) => void;
}) {
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);

  function kaydet(adres: SiparisAdresi) {
    if (yukleniyor) return;
    setYukleniyor(true);
    setHata(null);
    siparisAdresVer(siparis.id, adres)
      .then(onGuncel)
      .catch((e: unknown) => setHata(hataMetni(e, "Adres kaydedilemedi. Tekrar dene.")))
      .finally(() => setYukleniyor(false));
  }

  return (
    <AdresFormu
      gonderEtiketi="Adresi kaydet"
      onGonder={kaydet}
      yukleniyor={yukleniyor}
      hata={hata}
    />
  );
}

type AdimHali = "tamam" | "simdi" | "bekliyor" | "iptal";

interface Adim {
  anahtar: string;
  baslik: string;
  hali: AdimHali;
  ayrinti: string[];
}

function iadeMetni(s: Siparis): string {
  // F-GAM-16 / Emre S6: teslim edilemeyen sipariş de iptal edilirken puan AYNI işlemde iade edilir (sunucu: iptal
  // = iade, veritabanı CHECK'i). Her iptal nedeninde cümle aynı.
  return s.iptalNedeni === "UNDELIVERABLE"
    ? "Ürün teslim edilemedi; puanın iade edildi."
    : "Puanın iade edildi.";
}

/** Zaman çizelgesi adımları: yalnız sunucunun verdiği durum ve tarihlerden; ekran hiçbir geçiş icat etmez. */
export function zamanCizelgesi(s: Siparis): Adim[] {
  const verildi: Adim = {
    anahtar: "verildi",
    baslik: "Sipariş verildi",
    hali: "tamam",
    ayrinti: [anMetni(s.tarih)],
  };

  if (s.durum === "CANCELLED") {
    return [
      verildi,
      {
        anahtar: "iptal",
        baslik: "Sipariş iptal edildi",
        hali: "iptal",
        ayrinti: [
          ...(s.iptalTarihi ? [anMetni(s.iptalTarihi)] : []),
          ...(s.iptalNedeni ? [SIPARIS_IPTAL_NEDENI_ETIKETI[s.iptalNedeni]] : []),
          iadeMetni(s),
        ],
      },
    ];
  }

  const onayBekliyor = s.durum === "AWAITING_PARENT" || s.durum === "AWAITING_ADDRESS";
  const onay: Adim = {
    anahtar: "onay",
    baslik:
      s.durum === "AWAITING_PARENT"
        ? "Veli onayı bekleniyor"
        : s.durum === "AWAITING_ADDRESS"
          ? "Adres bekleniyor"
          : "Onay ve adres alındı",
    hali: onayBekliyor ? "simdi" : "tamam",
    ayrinti: onayBekliyor && s.sonGun ? [`Son gün: ${gunBicimi(s.sonGun)}`] : [],
  };

  const gonderildi = s.durum === "SHIPPED" || s.durum === "DELIVERED";
  const hazirlaniyor: Adim = {
    anahtar: "hazirlaniyor",
    baslik: "Hazırlanıyor",
    hali: s.durum === "PREPARING" ? "simdi" : gonderildi ? "tamam" : "bekliyor",
    ayrinti: [],
  };

  const kargoda: Adim = {
    anahtar: "kargoda",
    baslik: "Kargoda",
    hali: s.durum === "SHIPPED" ? "simdi" : s.durum === "DELIVERED" ? "tamam" : "bekliyor",
    ayrinti: gonderildi
      ? [
          ...(s.kargoFirmasi ? [`Kargo firması: ${s.kargoFirmasi}`] : []),
          ...(s.takipNo ? [`Takip no: ${s.takipNo}`] : []),
          ...(s.kargoTarihi ? [anMetni(s.kargoTarihi)] : []),
        ]
      : [],
  };

  const teslim: Adim = {
    anahtar: "teslim",
    baslik: "Teslim edildi",
    hali: s.durum === "DELIVERED" ? "tamam" : "bekliyor",
    ayrinti: s.durum === "DELIVERED" && s.teslimTarihi ? [anMetni(s.teslimTarihi)] : [],
  };

  return [verildi, onay, hazirlaniyor, kargoda, teslim];
}

const ISARET_SINIFI: Record<AdimHali, string> = {
  tamam: "border-onay-600 bg-onay-600 text-notr-0",
  simdi: "border-marka-600 bg-marka-50 text-marka-700",
  bekliyor: "border-notr-500 bg-notr-0",
  iptal: "border-hata-700 bg-hata-50 text-hata-700",
};

// Renk tek sinyal değil: simge ve ekran okuyucu metni de durumu söylüyor.
const HAL_METNI: Record<AdimHali, string> = {
  tamam: "tamamlandı",
  simdi: "şu an burada",
  bekliyor: "bekliyor",
  iptal: "iptal edildi",
};

function ZamanCizelgesi({ siparis }: { siparis: Siparis }) {
  return (
    <ol aria-label="Sipariş adımları" className="flex flex-col gap-4">
      {zamanCizelgesi(siparis).map((a) => (
        <li
          key={a.anahtar}
          aria-current={a.hali === "simdi" ? "step" : undefined}
          className="flex gap-3"
        >
          <span
            aria-hidden="true"
            className={cn(
              "rounded-rozet mt-0.5 flex size-6 shrink-0 items-center justify-center border-2",
              ISARET_SINIFI[a.hali],
            )}
          >
            {a.hali === "tamam" && <Ikon ad="tamam" boyut="kucuk" />}
            {a.hali === "simdi" && <Ikon ad="bekliyor" boyut="kucuk" />}
            {a.hali === "iptal" && <Ikon ad="hata" boyut="kucuk" />}
          </span>
          <div className="min-w-0">
            <p
              className={cn(
                "text-sm font-medium",
                a.hali === "bekliyor" ? "text-notr-600" : "text-notr-900",
              )}
            >
              {a.baslik}
              <span className="sr-only"> — {HAL_METNI[a.hali]}</span>
            </p>
            {a.ayrinti.map((r) => (
              <p key={r} className="text-notr-600 text-xs">
                {r}
              </p>
            ))}
          </div>
        </li>
      ))}
    </ol>
  );
}

export interface SiparisDetayProps {
  /** Listeden gelen ilk hâl; ekran açılınca sunucudan tazelenir. */
  siparis: Siparis;
  onGeri?: () => void;
  /** Sipariş değişince (adres verildi, iptal, tazeleme) üst ekran listeyi güncellesin. */
  onDegisti?: (s: Siparis) => void;
}

export function SiparisDetay({ siparis: ilk, onGeri, onDegisti }: SiparisDetayProps) {
  const kok = useUsteKaydir();
  const [s, setS] = useState<Siparis>(ilk);
  const [iptalSorusu, setIptalSorusu] = useState(false);
  const [iptalYukleniyor, setIptalYukleniyor] = useState(false);
  const [iptalHatasi, setIptalHatasi] = useState<string | null>(null);

  function guncelle(yeni: Siparis) {
    setS(yeni);
    onDegisti?.(yeni);
  }

  // Liste açıldığından beri durum değişmiş olabilir (kargoya verildi vb.). Tazeleme başarısızsa listedeki hâl
  // kalır; asıl koruma sunucuda (iptal/adres uçları durum uyuşmazsa reddeder ve ekran bunun metnini gösterir).
  useEffect(() => {
    let iptal = false;
    siparisGetir(ilk.id)
      .then((y) => {
        if (iptal) return;
        setS(y);
        onDegisti?.(y);
      })
      .catch(() => undefined);
    return () => {
      iptal = true;
    };
    // `onDegisti` bilerek bağımlılık değil: üst ekranın her çiziminde yeniden istek atmamalı.
  }, [ilk.id]);

  function iptalEt() {
    if (iptalYukleniyor) return;
    setIptalYukleniyor(true);
    setIptalHatasi(null);
    siparisIptalEt(s.id)
      .then((y) => {
        setIptalSorusu(false);
        guncelle(y);
      })
      .catch((e: unknown) => setIptalHatasi(hataMetni(e, "Sipariş iptal edilemedi. Tekrar dene.")))
      .finally(() => setIptalYukleniyor(false));
  }

  return (
    <div ref={kok} className="flex flex-col gap-3">
      {onGeri && (
        <div>
          <Buton gorunum="sessiz" onClick={onGeri}>
            Siparişlerime dön
          </Buton>
        </div>
      )}

      <Kart
        baslikSeviyesi={3}
        baslik={s.urunAdi}
        aciklama={`${s.puan} puan`}
        eylem={<Rozet ton={siparisRozetTonu(s.durum)}>{SIPARIS_DURUMU_ETIKETI[s.durum]}</Rozet>}
      >
        <h4 className="text-notr-600 mb-3 text-xs font-medium">Sipariş durumu</h4>
        <ZamanCizelgesi siparis={s} />
        {s.adresVerildi && (
          <p className="text-notr-600 mt-4 text-xs">
            Teslimat adresi alındı. Güvenliğin için adres bu ekranda gösterilmez.
          </p>
        )}
      </Kart>

      {s.durum === "AWAITING_PARENT" && (
        <NotKutusu ton="bilgi">
          Adresi velin verecek. Velin uygulamaya girip siparişi onaylayınca sipariş hazırlanmaya
          başlar.
          {s.sonGun ? ` Son gün: ${gunBicimi(s.sonGun)}.` : ""} Süre dolarsa sipariş iptal olur ve
          puanın iade edilir.
        </NotKutusu>
      )}

      {s.durum === "AWAITING_ADDRESS" && (
        <Kart
          baslikSeviyesi={4}
          baslik="Teslimat adresin"
          aciklama={
            s.sonGun
              ? `Adresini ${gunBicimi(s.sonGun)} tarihine kadar yaz; yazmazsan sipariş iptal olur ve puanın iade edilir.`
              : "Sipariş kargoyla bu adrese gönderilir."
          }
        >
          <AdresBolumu siparis={s} onGuncel={guncelle} />
        </Kart>
      )}

      {s.iptalEdilebilir &&
        (iptalSorusu ? (
          <Kart baslikSeviyesi={4} baslik="Siparişi iptal edelim mi?" ton="uyari">
            <p className="text-notr-700 text-sm">
              İptal edersen {s.puan} puanın iade edilir. Bu işlem geri alınamaz.
            </p>
            {iptalHatasi && (
              <p
                role="alert"
                className="rounded-alan bg-hata-50 text-hata-700 mt-2 px-3 py-2 text-sm"
              >
                {iptalHatasi}
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <Buton gorunum="tehlike" yukleniyor={iptalYukleniyor} onClick={iptalEt}>
                Evet, siparişi iptal et
              </Buton>
              <Buton
                gorunum="sessiz"
                disabled={iptalYukleniyor}
                onClick={() => {
                  setIptalSorusu(false);
                  setIptalHatasi(null);
                }}
              >
                Vazgeç
              </Buton>
            </div>
          </Kart>
        ) : (
          <div>
            <Buton gorunum="tehlike" onClick={() => setIptalSorusu(true)}>
              Siparişi iptal et
            </Buton>
          </div>
        ))}
    </div>
  );
}
